import { getTrackingLink } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { FulfillmentGroupData } from "./fulfillment-group";

/** The only fields tracking is derived from. */
export type FulfillmentGroupTrackingSource = Pick<
  FulfillmentGroupData,
  "shipment" | "additionalShipments" | "fulfillmentInfo" | "shipmentDetails"
>;

export interface FulfillmentGroupTracking {
  trackingNumber: string;
  carrier: string | null | undefined;
  /**
   * Empty when we can't build a link: carriers outside the code-derived set
   * (UPS, FedEx, USPS, DHL, OnTrac, ...) are tracked through a tracker-hosted
   * `publicUrl`, which a manually-fulfilled group never has.
   */
  link: string;
}

/**
 * Every tracking number on a group, in source precedence order, paired with the
 * carrier tracking link for each.
 *
 * A group carries tracking in one of three shapes: a purchased label
 * (`shipment` plus `additionalShipments` for multi-parcel), a manual
 * fulfillment (`fulfillmentInfo`, written from `manualFulfillmentInfo` — a
 * mark-as-fulfilled with a tracking code never populates `shipment`), or a
 * freight booking (`shipmentDetails.freightShipment`).
 */
export function getFulfillmentGroupTracking(
  row: FulfillmentGroupTrackingSource,
): FulfillmentGroupTracking[] {
  const tracking: FulfillmentGroupTracking[] = [];

  if (row.shipment?.tracker?.trackingCode) {
    tracking.push({
      trackingNumber: row.shipment.tracker.trackingCode,
      carrier: row.shipment.tracker.carrier,
      link: getTrackingLink(
        row.shipment.tracker.carrier,
        row.shipment.tracker.trackingCode,
        row.shipment.tracker.publicUrl,
        row.shipment.boughtRate?.service,
      ),
    });
    for (const additionalShipment of row.additionalShipments ?? []) {
      if (additionalShipment.tracker?.trackingCode) {
        tracking.push({
          trackingNumber: additionalShipment.tracker.trackingCode,
          carrier: additionalShipment.tracker.carrier,
          link: getTrackingLink(
            additionalShipment.tracker.carrier,
            additionalShipment.tracker.trackingCode,
            additionalShipment.tracker.publicUrl,
            additionalShipment.boughtRate?.service,
          ),
        });
      }
    }
  } else if (row.fulfillmentInfo?.trackingCode) {
    tracking.push({
      trackingNumber: row.fulfillmentInfo.trackingCode,
      carrier: row.fulfillmentInfo.carrier,
      link: getTrackingLink(
        row.fulfillmentInfo.carrier,
        row.fulfillmentInfo.trackingCode,
        "",
        row.fulfillmentInfo.service || undefined,
      ),
    });
  } else if (row.shipmentDetails?.freightShipment) {
    const { tracker } = row.shipmentDetails.freightShipment;
    tracking.push({
      trackingNumber: tracker.trackingCode,
      carrier: tracker.carrier,
      link: tracker.publicUrl,
    });
  }

  return dedupeByTrackingNumber(tracking);
}

export function getTrackingNumbers(
  row: FulfillmentGroupTrackingSource,
): string[] {
  return getFulfillmentGroupTracking(row).map(
    ({ trackingNumber }) => trackingNumber,
  );
}

function dedupeByTrackingNumber(
  tracking: FulfillmentGroupTracking[],
): FulfillmentGroupTracking[] {
  const byTrackingNumber = new Map<string, FulfillmentGroupTracking>();
  for (const entry of tracking) {
    if (!byTrackingNumber.has(entry.trackingNumber)) {
      byTrackingNumber.set(entry.trackingNumber, entry);
    }
  }
  return [...byTrackingNumber.values()];
}
